import { createContext, useEffect, ComponentType } from 'react';

import { NextPageContext } from 'next';
import Content from '../components/Content';
import { BlocksTree, Subtree } from '@finder/ui-blocks.commons';
import {
  Heading,
  filterHeadings,
  containsTableOfContentsBlock,
} from '../server/utilities/filter-headings';
import { init as lazyLoadImages } from '../modules/lazy-load';
import { TraceType } from '../components/types';

interface MultiRenderedPageProps {
  blocksTree: BlocksTree;
  hasTocBlock: boolean;
  TraceComponent?: ComponentType<TraceType>;
}

export interface PageContextProps {
  hasTocBlock: boolean;
  headings?: Heading[];
  isAdmin: boolean;
}

/**
 * If we include any more global state here, we should consider using
 * redux instead. Did not want to overcomplicate for just headings.
 */
export const PageContext = createContext<PageContextProps>(null);

interface RootProps extends Record<string, unknown> {
  isAdmin: boolean;
}

const isServer = typeof window === 'undefined';

export default function MultiRenderedPage({
  blocksTree,
  hasTocBlock,
  TraceComponent,
}: MultiRenderedPageProps): JSX.Element {
  if (blocksTree && blocksTree.root) {
    let headings: Heading[] = [];
    if (hasTocBlock) {
      headings = filterHeadings(blocksTree.root.subtree as Subtree[]);
    }
    const { isAdmin } = blocksTree.root.value.data as RootProps;

    const contextValue = {
      hasTocBlock,
      headings,
      isAdmin,
    };

    useEffect(() => {
      if (isServer) {
        return;
      }
      lazyLoadImages();
    }, [isServer]);

    if (isServer && TraceComponent) {
      return (
        <TraceComponent
          traceName="rendering.tree"
          Content={
            <PageContext.Provider value={contextValue}>
              <Content blocksTree={blocksTree} TraceComponent={TraceComponent} />
            </PageContext.Provider>
          }
        />
      );
    }

    return (
      <PageContext.Provider value={contextValue}>
        <Content blocksTree={blocksTree} />
      </PageContext.Provider>
    );
  }

  return <div>Nothing here</div>;
}

// TODO: add blocksTree type
MultiRenderedPage.getInitialProps = async ({
  req,
}: NextPageContext): Promise<MultiRenderedPageProps> => {
  const hasTocBlock = await containsTableOfContentsBlock(req['body']);

  let TraceComponent;
  if (isServer) {
    TraceComponent = await import('../components/Trace' /* webpackChunkName: "trace" */).then(
      (module) => module.default
    );
  }

  return { blocksTree: req['body'] as BlocksTree, hasTocBlock, TraceComponent };
};
